import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Loader2,
  AlertCircle,
  Info,
  Gift,
  User,
  Package,
  Sparkles,
  Heart,
  Mail,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { familyService, type Student, type CalculateBusinessDaysRequest, type BusinessDaysResponse } from "@/services";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface AssignTicketsModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  apoderadoId: string;
  onSuccess: () => void;
  orderId?: string; // ID de la orden opcional para asignar desde una orden específica
  // ✅ NUEVAS PROPS PARA REUTILIZACIÓN
  mode?: 'assign-to-other' | 'assign-to-self'; // Modo de uso del modal
  currentUserId?: string; // ID del usuario actual (para modo self)
  currentUserRoles?: string[]; // Roles del usuario actual
}

interface TicketPack {
  id: string;
  saldo_restante: number;
  producto_id_canjeable: string;
  producto?: {
    nombre: string;
    precio: number;
  };
}

export const AssignTicketsModal: React.FC<AssignTicketsModalProps> = ({
  isOpen,
  onClose,
  student,
  apoderadoId,
  onSuccess,
  orderId,
  // ✅ NUEVAS PROPS
  mode = 'assign-to-other',
  currentUserId,
  currentUserRoles = [],
}) => {
  const [ticketPacks, setTicketPacks] = useState<TicketPack[]>([]);
  const [selectedPack, setSelectedPack] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [businessDays, setBusinessDays] = useState<BusinessDaysResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDaysList, setShowDaysList] = useState(false); // ✅ Nuevo estado para mostrar/ocultar días

  // ✅ DETERMINAR EL USUARIO BENEFICIARIO SEGÚN EL MODO
  const getBeneficiaryUserId = (): string => {
    if (mode === 'assign-to-self') {
      return currentUserId || '';
    }
    // Modo assign-to-other (apoderado asignando a hijo)
    return student?.id || '';
  };

  // ✅ DETERMINAR EL USUARIO QUE PROGRAMA (OWNER)
  const getOwnerUserId = (): string => {
    if (mode === 'assign-to-self') {
      return currentUserId || '';
    }
    // Modo assign-to-other (apoderado)
    return apoderadoId;
  };

  // ✅ DETERMINAR EL TÍTULO DEL MODAL
  const getModalTitle = (): string => {
    if (mode === 'assign-to-self') {
      return 'Programar Pack para Mí';
    }
    return 'Asignar Pack a Estudiante';
  };

  // ✅ DETERMINAR LA DESCRIPCIÓN DEL MODAL
  const getModalDescription = (): string => {
    if (mode === 'assign-to-self') {
      return 'Programa tu pack comprado para recibirlo en fechas específicas';
    }
    return 'Asigna un pack de tickets a un estudiante para que pueda canjearlo por pedidos';
  };

  /**
   * Carga los packs de tickets disponibles
   */
  const loadTicketPacks = async () => {
    if (!isOpen || !getOwnerUserId()) return;

    setIsLoading(true);
    setError(null);

    try {
      const packs = await familyService.getTicketPacks();
      setTicketPacks(packs);

      // Seleccionar el primer pack por defecto si hay disponibles
      if (packs.length > 0 && !selectedPack) {
        setSelectedPack(packs[0].id);
      }
    } catch (err: any) {
      setError("Error al cargar los packs de tickets");
      console.error("Error loading ticket packs:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Carga los packs cuando se abre el modal
   */
  useEffect(() => {
    if (isOpen) {
      loadTicketPacks();
    }
  }, [isOpen, getOwnerUserId()]);

  /**
   * Calcula los días hábiles cuando cambia el pack o la fecha
   */
  useEffect(() => {
    if (selectedPack && startDate) {
      calculateBusinessDays();
    } else {
      setBusinessDays(null);
    }
  }, [selectedPack, startDate]);

  /**
   * Obtiene el pack seleccionado
   */
  const getSelectedPack = (): TicketPack | null => {
    return ticketPacks.find((pack) => pack.id === selectedPack) || null;
  };

  /**
   * Formatea el precio como moneda chilena
   */
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  /**
   * Formatea una fecha para mostrar
   */
  const formatDate = (date: Date): string => {
    return format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es });
  };

  /**
   * Calcula los días hábiles usando el endpoint
   */
  const calculateBusinessDays = async () => {
    const pack = getSelectedPack();
    if (!pack || !startDate) return;

    setIsCalculating(true);
    setError(null);

    try {
      // Asegurar que la fecha se envíe correctamente sin problemas de zona horaria
      const request: CalculateBusinessDaysRequest = {
        startDate: format(startDate, "yyyy-MM-dd"),
        packSize: pack.saldo_restante,
      };

      console.log('📅 [FRONTEND] Enviando request:', {
        startDate: request.startDate,
        packSize: request.packSize,
        originalStartDate: startDate.toISOString()
      });

      const result = await familyService.calculateBusinessDays(request);
      
      // Procesar las fechas que vienen del backend correctamente
      const processedBusinessDays = {
        ...result,
        businessDays: result.businessDays.map((dateStr: any) => {
          // Si ya es un Date, usarlo directamente
          if (dateStr instanceof Date) {
            return dateStr;
          }
          // Si es un string, parsearlo correctamente
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11
        })
      };

      console.log('📅 [FRONTEND] Fechas procesadas:', {
        originalBusinessDays: result.businessDays,
        processedBusinessDays: processedBusinessDays.businessDays.map(d => d.toISOString())
      });

      setBusinessDays(processedBusinessDays);
    } catch (err: any) {
      setError(err.message || "Error al calcular días hábiles");
      setBusinessDays(null);
    } finally {
      setIsCalculating(false);
    }
  };

  /**
   * Valida el formulario antes de enviar
   */
  const validateForm = (): boolean => {
    if (!selectedPack) {
      setError("Debes seleccionar un pack de tickets");
      return false;
    }
    if (!startDate) {
      setError("Debes seleccionar una fecha inicial");
      return false;
    }
    if (!businessDays) {
      setError("Debes esperar a que se calculen los días hábiles");
      return false;
    }
    if (mode === 'assign-to-other' && !student) {
      setError("Debes seleccionar un estudiante");
      return false;
    }
    if (mode === 'assign-to-self' && !currentUserId) {
      setError("Error: ID de usuario no disponible");
      return false;
    }
    return true;
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      console.log('🚀 [FRONTEND] Iniciando programación de pack:', {
        packId: selectedPack,
        startDate: format(startDate!, "yyyy-MM-dd"),
        beneficiaryUserId: getBeneficiaryUserId(),
        packSize: getSelectedPack()?.saldo_restante,
        mode: mode
      });

      const result = await familyService.schedulePack(
        selectedPack,
        format(startDate!, "yyyy-MM-dd"),
        getBeneficiaryUserId()
      );

      console.log('✅ [FRONTEND] Pack programado exitosamente:', result);

      // Recargar packs después de la programación
      await loadTicketPacks();

      // Resetear formulario
      setStartDate(undefined);
      setBusinessDays(null);
      setShowDaysList(false);

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('❌ [FRONTEND] Error al programar pack:', {
        error: err,
        message: err.message,
        code: err.code,
        status: err.response?.status,
        data: err.response?.data
      });
      
      // Mostrar error más detallado
      let errorMessage = "Error al programar el pack";
      
      if (err.code === "ECONNABORTED") {
        errorMessage = "Tiempo de espera agotado. El pack puede haberse programado correctamente. Verifica el historial del estudiante.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.code === "NETWORK_ERROR") {
        errorMessage = "Error de conexión. Verifica tu internet.";
      }
      
      setError(errorMessage);
      
      // Si es un timeout, sugerir verificar el historial
      if (err.code === "ECONNABORTED") {
        console.log('⚠️ [FRONTEND] Timeout detectado - sugiriendo verificar historial');
        // Aquí podrías mostrar un toast o notificación adicional
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedPack("");
      setStartDate(undefined);
      setBusinessDays(null);
      setError(null);
      onClose();
    }
  };

  if (mode === 'assign-to-other' && !student) return null;
  if (mode === 'assign-to-self' && !currentUserId) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-gradient-to-br from-card/90 to-card/70 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-2xl">
        <DialogHeader className="pb-4 sm:pb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Gift className="h-5 w-5 sm:h-6 sm:w-6 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                {orderId
                  ? `Programar desde Orden ${orderId.slice(0, 8)}...`
                  : getModalTitle()}
              </DialogTitle>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {getModalDescription()}
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Mensaje de error mejorado */}
          {error && (
            <Alert
              variant="destructive"
              className="border-red-500/20 bg-red-500/10 rounded-xl sm:rounded-2xl"
            >
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {/* Información del usuario - Mobile Optimized */}
          {mode === 'assign-to-other' && student && (
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-primary">
                    Estudiante
                  </span>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {student.nombre} {student.apellido}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Mail className="h-3 w-3" />
                <span className="truncate">{student.email}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Package className="h-3 w-3 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-600">
                  Productos disponibles: {student.ticketBalance || 0}
                </span>
              </div>
            </div>
          )}

          {mode === 'assign-to-self' && (
            <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-blue-600">
                    Programando para mí
                  </span>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {currentUserRoles?.includes('Estudiante') ? 'Estudiante' : 
                     currentUserRoles?.includes('Funcionario') ? 'Funcionario' : 'Usuario'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Package className="h-3 w-3 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-600">
                  Programando pack para recibir en fechas específicas
                </span>
              </div>
            </div>
          )}

          {/* Seleccionar Producto mejorado - Mobile Optimized */}
          <div className="space-y-2 sm:space-y-3">
            <Label
              htmlFor="ticketPack"
              className="text-sm font-medium flex items-center gap-2"
            >
              <Gift className="h-4 w-4 text-muted-foreground" />
              Pack a Programar *
            </Label>
            {isLoading ? (
              <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-muted/30 rounded-xl sm:rounded-2xl border border-border/50">
                <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin text-primary" />
                <span className="text-xs sm:text-sm text-muted-foreground">
                  Cargando packs disponibles...
                </span>
              </div>
            ) : ticketPacks.length === 0 ? (
              <Alert className="border-amber-500/20 bg-amber-500/10 rounded-xl sm:rounded-2xl">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-600">
                  No hay packs disponibles para programar
                </AlertDescription>
              </Alert>
            ) : (
              <Select value={selectedPack} onValueChange={setSelectedPack}>
                <SelectTrigger className="h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm">
                  <SelectValue placeholder="Selecciona un pack" />
                </SelectTrigger>
                <SelectContent className="bg-card/90 backdrop-blur-xl border border-border/50 rounded-xl sm:rounded-2xl">
                  {ticketPacks.map((pack) => (
                    <SelectItem
                      key={pack.id}
                      value={pack.id}
                      className="hover:bg-primary/10 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="font-medium text-sm">
                          {pack.producto?.nombre ||
                            `Pack ${pack.id.slice(0, 8)}`}
                        </span>
                        <Badge
                          variant="outline"
                          className="ml-2 bg-emerald-500/10 border-emerald-500/20 text-emerald-600 text-xs"
                        >
                          {pack.saldo_restante} unidades
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Información del pack seleccionado mejorada - Mobile Optimized */}
          {getSelectedPack() && (
            <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-emerald-600">
                    {getSelectedPack()?.producto?.nombre || "Pack"}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {formatPrice(getSelectedPack()?.producto?.precio || 0)} por
                    unidad
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                <span>
                  Unidades disponibles: {getSelectedPack()?.saldo_restante}
                </span>
              </div>
            </div>
          )}

          {/* Fecha inicial mejorada - Mobile Optimized */}
          <div className="space-y-2 sm:space-y-3">
            <Label
              htmlFor="startDate"
              className="text-sm font-medium flex items-center gap-2"
            >
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Fecha Inicial *
            </Label>
            <DatePicker
              date={startDate}
              onDateChange={setStartDate}
              placeholder="Selecciona fecha inicial"
              disabled={isSubmitting || !selectedPack}
            />
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              Solo días hábiles (lunes a viernes). Se programarán {getSelectedPack()?.saldo_restante || 0} pedidos automáticamente.
            </p>
          </div>

          {/* Cálculo de días hábiles */}
          {isCalculating && (
            <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-500/10 rounded-xl sm:rounded-2xl border border-blue-500/20">
              <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin text-blue-600" />
              <span className="text-xs sm:text-sm text-blue-600">
                Calculando días hábiles...
              </span>
            </div>
          )}

          {/* Resumen de programación mejorado - Mobile Optimized */}
          {businessDays && (
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">
                  Resumen de Programación
                </span>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Pack seleccionado:</span>
                  <span className="font-medium text-foreground">
                    {getSelectedPack()?.producto?.nombre || "Pack"}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Unidades a programar:</span>
                  <span className="font-medium text-foreground">
                    {businessDays.packSize}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Fecha inicial:</span>
                  <span className="font-medium text-foreground">
                    {formatDate(businessDays.actualStartDate)}
                  </span>
                </div>
                
                <div className="border-t border-border/50 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowDaysList(!showDaysList)}
                    className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200 mb-2"
                  >
                    {showDaysList ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    Se programarán pedidos para los días:
                    <Badge variant="outline" className="ml-2 bg-primary/10 border-primary/20 text-primary text-xs">
                      {businessDays.businessDays.length} días
                    </Badge>
                  </button>
                  
                  {showDaysList && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {businessDays.businessDays.map((date: Date, index: number) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-2 bg-background/50 rounded-lg border border-border/30"
                        >
                          <CheckCircle className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground">
                            {formatDate(date)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer con botones mejorados - Mobile Optimized */}
          <DialogFooter className="pt-4 sm:pt-6">
            <div className="flex gap-2 sm:gap-3 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 h-10 sm:h-12 hover:bg-primary/10 transition-all duration-200 rounded-xl sm:rounded-2xl text-sm"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting || !selectedPack || !startDate || !businessDays
                }
                className="flex-1 h-10 sm:h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl rounded-xl sm:rounded-2xl text-sm"
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                <Gift className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Programar Pack</span>
                <span className="sm:hidden">Programar</span>
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
